import { useCallback, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { ApiError } from '../../../lib/api';
import { getErrorMessage } from '../../../lib/errorMessage';
import { Order, OrderStatus } from '../types/orders.types';
import { ordersApi } from '../api/orders.api';
import { resolveCanonicalOrderId } from '../../../lib/order.util';
import type { CheckoutPreviewPricingLock, OrderQuoteSnapshot } from '../types/orders.types';
import type { ShopperPreferences } from '../../shopper/types/shopper.types';
import { normalizeCartLines } from '../../cart/utils/cartNormalization';

type ToastType = 'info' | 'success' | 'warning' | 'error';

interface useOrderHooksParams {
  addToast: (message: string, type?: ToastType) => void;
  setOrders: Dispatch<SetStateAction<Order[]>>;
  /** Re-sync server-owned order lists after a confirmed mutation. */
  onOrdersChanged: () => Promise<void>;
}

const normalizeDeliveryAddressInput = (deliveryAddress: unknown): string | Record<string, unknown> => {
  if (typeof deliveryAddress === 'string') return deliveryAddress;
  if (deliveryAddress && typeof deliveryAddress === 'object') return deliveryAddress as Record<string, unknown>;
  return '';
};

const resolveShippingAddress = (deliveryAddress: unknown): string => {
  if (typeof deliveryAddress === 'string') return deliveryAddress.trim();
  if (deliveryAddress && typeof deliveryAddress === 'object' && 'address' in deliveryAddress) {
    return String((deliveryAddress as { address?: unknown }).address || '').trim();
  }
  return '';
};

const readOrderFromEnvelope = (payload: unknown): Order | null => {
  if (!payload || typeof payload !== 'object') return null;
  if ('data' in payload && payload.data && typeof payload.data === 'object') {
    const data = payload.data as { order?: unknown };
    if (data.order && typeof data.order === 'object') {
      return data.order as Order;
    }
    return payload.data as Order;
  }
  return payload as Order;
};

const ensureCanonicalOrderId = (order: Order): Order => {
  const canonicalId = resolveCanonicalOrderId(order);
  return canonicalId ? ({ ...order, id: canonicalId } as Order) : order;
};

const createIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `ninpo-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const useOrderHooks = ({ addToast, setOrders, onOrdersChanged }: useOrderHooksParams) => {
  /** Reuse one place-order key while an attempt is in flight (double-click / retry). */
  const placeOrderIdempotencyKeyRef = useRef<string | null>(null);

  const buildQuoteSnapshot = (pricingLock: CheckoutPreviewPricingLock | null): OrderQuoteSnapshot => {
    const serverSnap = pricingLock?.orderQuoteSnapshot;
    if (serverSnap && typeof serverSnap === 'object') {
      const lowFromLock =
        typeof pricingLock?.lowConfidenceAccepted === 'boolean' ? pricingLock.lowConfidenceAccepted : undefined;
      const lowFromNested =
        typeof pricingLock?.checkoutAcknowledgment?.lowConfidenceAccepted === 'boolean'
          ? pricingLock.checkoutAcknowledgment.lowConfidenceAccepted
          : undefined;
      const lowFromServer =
        typeof serverSnap.checkoutAcknowledgment?.lowConfidenceAccepted === 'boolean'
          ? serverSnap.checkoutAcknowledgment.lowConfidenceAccepted
          : false;
      const disclaimerFromLock = pricingLock?.uncertainty?.disclaimer;
      const disclaimerFromServer = serverSnap.uncertainty?.disclaimer;
      return {
        ...serverSnap,
        quoteId: pricingLock?.quoteId || serverSnap.quoteId,
        generatedAt: pricingLock?.generatedAt ?? serverSnap.generatedAt,
        uncertainty: {
          ...serverSnap.uncertainty,
          disclaimer: {
            version: disclaimerFromLock?.version ?? disclaimerFromServer?.version ?? '',
            message: disclaimerFromLock?.message ?? disclaimerFromServer?.message ?? '',
            acknowledgedAtQuote:
              disclaimerFromLock?.acknowledgedAtQuote ??
              disclaimerFromServer?.acknowledgedAtQuote ??
              false
          }
        },
        checkoutAcknowledgment: {
          lowConfidenceAccepted: lowFromLock ?? lowFromNested ?? lowFromServer,
          retailerAuthorizationAccepted:
            pricingLock?.retailerAuthorizationAccepted
            ?? pricingLock?.checkoutAcknowledgment?.retailerAuthorizationAccepted
            ?? serverSnap.checkoutAcknowledgment?.retailerAuthorizationAccepted
            ?? false,
          lineSubstituteAccepted:
            pricingLock?.lineSubstituteAccepted
            ?? pricingLock?.checkoutAcknowledgment?.lineSubstituteAccepted
            ?? serverSnap.checkoutAcknowledgment?.lineSubstituteAccepted
            ?? false
        }
      };
    }

    throw new Error(
      'Missing server checkout quote (orderQuoteSnapshot). Refresh checkout preview and pass it on the pricing lock before placing an order.'
    );
  };

  const updateOrder = useCallback(
    async (id: string, status: OrderStatus, metadata?: Record<string, unknown>): Promise<boolean> => {
      const orderId = String(id || '').trim();
      if (!orderId) {
        addToast('Order update failed: missing order id', 'warning');
        return false;
      }

      let previousOrder: Order | null = null;
      setOrders(prev => {
        previousOrder = prev.find(order => resolveCanonicalOrderId(order) === orderId) ?? null;
        return prev.map(order => {
          const canonicalId = resolveCanonicalOrderId(order);
          if (canonicalId !== orderId) return order;
          return {
            ...order,
            id: canonicalId,
            status,
            ...(metadata || {})
          };
        });
      });

      try {
        const serverOrderPayload = await ordersApi.update(
          orderId,
          { status, ...(metadata || {}) },
          createIdempotencyKey()
        );
        const serverOrder = readOrderFromEnvelope(serverOrderPayload);

        if (serverOrder && typeof serverOrder === 'object') {
          const canonicalServerId = resolveCanonicalOrderId(serverOrder);
          setOrders(prev =>
            prev.map(order => {
              const canonicalId = resolveCanonicalOrderId(order);
              const mergeTargetId = canonicalServerId ?? orderId;
              if (canonicalId !== orderId && canonicalId !== mergeTargetId) return order;
              return ensureCanonicalOrderId({
                ...order,
                ...serverOrder,
                id: mergeTargetId
              } as Order);
            })
          );
        }
        addToast(`Order ${orderId} updated to ${status}`, 'success');
        await onOrdersChanged();
        return true;
      } catch (error) {
        if (previousOrder) {
          setOrders(prev =>
            prev.map(order => (resolveCanonicalOrderId(order) === orderId ? ensureCanonicalOrderId(previousOrder as Order) : order))
          );
        }
        const message = getErrorMessage(error, 'Order update failed to sync');
        addToast(message, 'warning');
        return false;
      }
    },
    [addToast, setOrders, onOrdersChanged]
  );

  const getCheckoutPreview = useCallback(
    async (cartItems: { id: string; quantity: number }[], deliveryAddress: unknown) => {
      try {
        // normalizeCartLines(cartItems).map(item => ({ productId: item.productId, quantity: item.quantity }))
        const mappedCartItems = normalizeCartLines(cartItems).map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          temperaturePreference: item.temperaturePreference
        }));
        return await ordersApi.previewCheckout(
          {
            cartItems: mappedCartItems,
            deliveryAddress: normalizeDeliveryAddressInput(deliveryAddress)
          },
          createIdempotencyKey()
        );
      } catch (e: unknown) {
        const message = getErrorMessage(e, 'Could not get checkout preview');
        addToast(message, 'warning');
        return null;
      }
    },
    [addToast]
  );

  const createOrder = useCallback(
    async (
      cartItems: { id: string; quantity: number }[],
      deliveryAddress: unknown,
      _paymentMethod: string,
      pricingLock: CheckoutPreviewPricingLock | null,
        orderOptions?: {
        walletCreditToReserveCents?: number;
        returnPayoutMethod?: 'CREDIT' | 'CASH';
        shopperPreferences?: ShopperPreferences;
        deliveryInstructions?: string;
        requestedDeliveryWindowId?: string;
        deliverySpeed?: 'STANDARD' | 'PRIORITY_DIRECT';
        tipCents?: number;
        promoCode?: string;
        savedAddressId?: string;
        savedPaymentMethodId?: string;
        guestEmail?: string;
        guestPhone?: string;
        isStandaloneReturn?: boolean;
        returnUpcs?: Array<{ upc: string; quantity: number } | string>;
        acceptedPolicies?: boolean;
        termsVersion?: string;
        privacyVersion?: string;
      }
    ) => {
      try {
        const shippingAddress = resolveShippingAddress(deliveryAddress);
        if (!shippingAddress) {
          throw new Error('Delivery address is required before checkout.');
        }
        if (!pricingLock?.selectedStoreId) {
          throw new Error('Store selection is required before checkout.');
        }
        const idempotencyKey =
          placeOrderIdempotencyKeyRef.current || createIdempotencyKey();
        placeOrderIdempotencyKeyRef.current = idempotencyKey;
        // normalizeCartLines(cartItems).map(item => ({ productId: item.productId, quantity: item.quantity }))
        const mappedCartItems = normalizeCartLines(cartItems).map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          temperaturePreference: item.temperaturePreference
        }));

        const orderBody = {
          items: mappedCartItems,
          isStandaloneReturn: orderOptions?.isStandaloneReturn,
          returnUpcs: orderOptions?.returnUpcs,
          shippingAddress,
          deliveryInstructions: orderOptions?.deliveryInstructions,
          requestedDeliveryWindowId: orderOptions?.requestedDeliveryWindowId,
          deliverySpeed: orderOptions?.deliverySpeed,
          tipCents: orderOptions?.tipCents,
          promoCode: orderOptions?.promoCode,
          savedAddressId: orderOptions?.savedAddressId,
          savedPaymentMethodId: orderOptions?.savedPaymentMethodId,
          selectedStoreId: pricingLock.selectedStoreId,
          extraStoreFeeEstimate:
            typeof pricingLock?.extraStoreFeeEstimate === 'number'
              ? pricingLock.extraStoreFeeEstimate
              : undefined,
          walletCreditToReserveCents: orderOptions?.walletCreditToReserveCents,
          returnPayoutMethod: orderOptions?.returnPayoutMethod,
          shopperPreferences: orderOptions?.shopperPreferences,
          quoteId: pricingLock?.quoteId || pricingLock?.orderQuoteSnapshot?.quoteId,
          quoteSnapshot: buildQuoteSnapshot(pricingLock),
          ...(orderOptions?.acceptedPolicies
            ? {
                acceptedPolicies: true as const,
                termsVersion: orderOptions.termsVersion,
                privacyVersion: orderOptions.privacyVersion
              }
            : {})
        };

        const guestEmail = String(orderOptions?.guestEmail || '').trim();
        const payloadToSend = guestEmail
          ? { ...orderBody, guestEmail, guestPhone: orderOptions?.guestPhone }
          : orderBody;
        const orderPayload = await ordersApi.create(payloadToSend, idempotencyKey);

        const orderData = readOrderFromEnvelope(orderPayload);
        const orderId = String(resolveCanonicalOrderId(orderData) || '').trim();
        if (!orderId) throw new Error('Order created without a valid order id');

        const embeddedSession =
          orderPayload && typeof orderPayload === 'object' && 'data' in orderPayload
            ? (orderPayload as { data?: { checkoutSession?: { url?: string; id?: string }; status?: string } }).data
                ?.checkoutSession
            : null;
        const orderStatus = String(
          (orderData as { internalStatus?: string; status?: string } | null | undefined)?.internalStatus ||
            (orderData as { status?: string } | null | undefined)?.status ||
            ''
        )
          .trim()
          .toUpperCase();
        const alreadyAuthorized = ['AUTHORIZED', 'CAPTURING', 'CAPTURED', 'DELIVERED'].includes(
          orderStatus
        );

        const isTerminalFailed = ['AUTH_FAILED', 'CANCELED', 'EXPIRED', 'FAILED'].includes(
          orderStatus
        );
        if (isTerminalFailed) {
          placeOrderIdempotencyKeyRef.current = null;
          throw new Error(
            'Payment authorization failed for this order. Please try again with a different payment method.'
          );
        }

        let checkoutSession: unknown;
        if (embeddedSession?.url) {
          checkoutSession = embeddedSession;
        } else if (alreadyAuthorized) {
          // Saved-card / wallet path already authorized — do not open a second Stripe session.
          checkoutSession = null;
        } else {
          let checkoutMandate:
            | {
                payload: {
                  orderId: string;
                  maxAmountCents: number;
                  agentId: string;
                  timestamp: number;
                };
                signature: string;
              }
            | undefined;
          try {
            const { readLocalAgentKeys, signCheckoutMandate } = await import(
              '../../auth/lib/agentKeyCrypto'
            );
            const local = readLocalAgentKeys();
            if (local.privateKeyPem && local.agentId) {
              const dollars = Number(
                (orderData as { total?: number; totalPrice?: number; totalCents?: number } | null)
                  ?.total ??
                  (orderData as { totalPrice?: number } | null)?.totalPrice ??
                  0
              );
              const fromCents = Number(
                (orderData as { totalCents?: number } | null)?.totalCents
              );
              const totalCents =
                Number.isFinite(fromCents) && fromCents > 0
                  ? Math.round(fromCents)
                  : Math.max(1, Math.round(dollars * 100));
              checkoutMandate = await signCheckoutMandate({
                privateKeyPem: local.privateKeyPem,
                orderId,
                maxAmountCents: totalCents,
                agentId: local.agentId
              });
            }
          } catch {
            checkoutMandate = undefined;
          }
          try {
            const sessionPayload = await ordersApi.createCheckoutSession(
              orderId,
              createIdempotencyKey(),
              checkoutMandate ? { checkoutMandate } : undefined
            );
            checkoutSession =
              typeof sessionPayload === 'object' && sessionPayload !== null && 'data' in sessionPayload
                ? (sessionPayload as { data?: unknown }).data
                : sessionPayload;

            // When merchant issued a Checkout JWT, upgrade to AP2 mandate (fail-open).
            // Only runs if AGENT_PRIVATE_KEY is configured server-side (checkoutJwt present).
            const sessionData = checkoutSession as {
              checkoutJwt?: string;
              url?: string;
            } | null;
            if (sessionData?.checkoutJwt) {
              try {
                const { readLocalAgentKeys, signAp2CheckoutMandate } = await import(
                  '../../auth/lib/agentKeyCrypto'
                );
                const local = readLocalAgentKeys();
                if (local.privateKeyPem && local.agentId) {
                  const dollars = Number(
                    (orderData as { total?: number; totalPrice?: number } | null)?.total ??
                      (orderData as { totalPrice?: number } | null)?.totalPrice ??
                      0
                  );
                  const fromCents = Number(
                    (orderData as { totalCents?: number } | null)?.totalCents
                  );
                  const totalCents =
                    Number.isFinite(fromCents) && fromCents > 0
                      ? Math.round(fromCents)
                      : Math.max(1, Math.round(dollars * 100));
                  const ap2CheckoutMandate = await signAp2CheckoutMandate({
                    privateKeyPem: local.privateKeyPem,
                    checkoutJwt: sessionData.checkoutJwt,
                    orderId,
                    maxAmountCents: totalCents,
                    agentId: local.agentId
                  });
                  const ap2Payload = await ordersApi.createCheckoutSession(
                    orderId,
                    createIdempotencyKey(),
                    { ap2CheckoutMandate }
                  );
                  checkoutSession =
                    typeof ap2Payload === 'object' && ap2Payload !== null && 'data' in ap2Payload
                      ? (ap2Payload as { data?: unknown }).data
                      : ap2Payload;
                }
              } catch {
                // Keep first (legacy or unsigned) session URL — AP2 is optional.
              }
            }
          } catch (mandateErr) {
            // Fail open: a bad/stale local agent key must not block human Stripe checkout.
            if (checkoutMandate) {
              const sessionPayload = await ordersApi.createCheckoutSession(
                orderId,
                createIdempotencyKey()
              );
              checkoutSession =
                typeof sessionPayload === 'object' &&
                sessionPayload !== null &&
                'data' in sessionPayload
                  ? (sessionPayload as { data?: unknown }).data
                  : sessionPayload;
            } else {
              throw mandateErr;
            }
          }
        }

        addToast('Order created successfully!', 'success');
        placeOrderIdempotencyKeyRef.current = null;
        return {
          order: orderData,
          checkoutSession
        };
      } catch (e: unknown) {
        // Keep the same key for immediate retries of the same attempt (network blip).
        // Hard validation failures should mint a new key next click — clear after toast.
        const message = getErrorMessage(e, 'Could not create order');
        addToast(message, 'warning');
        const isStaleSnapshot =
          (e instanceof ApiError && (e.code === 'TIER_SNAPSHOT_STALE' || e.code === 'PRICING_SNAPSHOT_STALE')) ||
          /stale/i.test(message);
        const isConflict = e instanceof ApiError && e.status === 409;
        if (isStaleSnapshot || isConflict || !(e instanceof ApiError) || (e.status !== 429 && e.status < 500)) {
          placeOrderIdempotencyKeyRef.current = null;
        }
        return null;
      }
    },
    [addToast]
  );

  return { updateOrder, getCheckoutPreview, createOrder };
};
